// Keyboard handler for ARIA tab patterns. Once an element advertises
// `role="tablist"`, the W3C ARIA Authoring Practices spec says users
// expect Left/Right arrows to move between tabs and Home/End to jump
// to the first/last. Without this handler the announce-vs-behaviour
// gap leaves keyboard and screen-reader users stranded.
//
// Usage:
//   const handleKeyDown = makeTablistKeyHandler(items.map(i => i.key), activeKey, setActive);
//   <div role="tablist" onKeyDown={handleKeyDown}>...</div>
//   <button role="tab" tabIndex={activeKey === item.key ? 0 : -1} ...>
//
// The `tabIndex={active ? 0 : -1}` (roving tabindex) is essential —
// without it Tab steps through every tab button before leaving the
// strip, which is exactly what we're trying to avoid.
//
// DOM focus is moved by indexing into
// `tablist.querySelectorAll('[role="tab"]')`, not by element id. So the
// caller doesn't need to give each tab a stable id for this helper to
// work, but pairing each tab with `aria-controls` pointing at its
// panel is still recommended for the wider ARIA tabs pattern.

export const makeTablistKeyHandler = <K extends string>(
	keys: readonly K[],
	active: K,
	setActive: (k: K) => void
) => {
	return (e: React.KeyboardEvent<HTMLElement>): void => {
		const idx = keys.indexOf(active);
		if (idx === -1) return;
		let next: number | null = null;
		if (e.key === "ArrowRight" || e.key === "ArrowDown") {
			next = (idx + 1) % keys.length;
		} else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
			next = (idx - 1 + keys.length) % keys.length;
		} else if (e.key === "Home") {
			next = 0;
		} else if (e.key === "End") {
			next = keys.length - 1;
		}
		if (next !== null) {
			e.preventDefault();
			const target = keys[next] as K;
			setActive(target);
			// Move DOM focus too so the active tab is the focused tab —
			// the surrounding tablist must give each tab an `id` of the
			// form `${baseId}-${key}` for this to work.
			const tablist = e.currentTarget;
			const buttons =
				tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]');
			buttons[next]?.focus();
		}
	};
};
